import {styled} from "@mui/system";
import {Box, Grid, Typography} from "@mui/material";
import React from "react";
import CCOContainer from "../../fundamentals/CCOContainer";
import CCORichText from "../../fundamentals/CCORichText/CCORichText";
import PreviewElementBoxDrawer from "../../preview/PreviewElementBoxDrawer";
import {metaDataProperty} from "../../preview/previewMetaData";
import {usePreviewContext} from "../../../context/PreviewContext";
import useWindowSize from "../../../utility/useWindowSize";
import CCOResponsivePicture from "../../fundamentals/CCOResponsivePicture";
import {C014StageCarouselPictureSettings} from "../../../common/constants/pictureSettings";
import {MARGINS} from "../../fundamentals/CCORichText/CCORichTextStyles";
import TeaserCardCallToAction, {CallToActionType} from "../C020TeaserCarousel/TeaserCardCallToAction";
import {getAltText} from "../../fundamentals/CCOPicture";

interface Props {
    item?: any
    hero?: boolean
    dataCypress?: string
}

const headlineStyle = (px: number) => ({
    background: 'white',
    px: px,
    py: 2,
});

const titleBoxStyle = {
    height: 'auto',
    marginTop: '16px'
}

const titleStyle = (padding: string, isWindowExtraSmallOrSmall: boolean, px: number) => ({
    '& span': {
        background: 'white',
        my: 0,
        py: padding,
        px: px,
        boxDecorationBreak: 'clone',
        lineHeight: isWindowExtraSmallOrSmall ? '2.1rem' : '2.7rem',
    },
    m: 0,
    p: 0
});

const textBoxStyle = (marginTop: number, marginBottom: string, padding: number) => ({
    background: 'white',
    p: padding,
    marginTop: marginTop,
    width: 'max-content',
    maxWidth: '100%',
    'div:last-child': {
        marginBottom: marginBottom,
    },
});

function C014StageTeaser(props: Props) {
    const Placeholder = styled("div")(({ theme }) => ({
        height: props.hero ? '658px' : '540px',
        [theme.breakpoints.down('xl')]: {
            height: '514px'
        },
        [theme.breakpoints.down('lg')]: {
            height: '386px'
        },
        [theme.breakpoints.down('md')]: {
            height: '538px'
        },
        [theme.breakpoints.down('sm')]: {
            height: '530px'
        },
    }));

    const HeroImage = styled("div")(({theme}) => ({
        position: "relative",
        height: props.hero ? '658px' : '540px',
        [theme.breakpoints.down('xl')]: {
            height: '514px'
        },
        [theme.breakpoints.down('lg')]: {
            height: '386px'
        },
        [theme.breakpoints.down('md')]: {
            height: '338px'
        },
        [theme.breakpoints.down('sm')]: {
            height: '330px'
        },
        display: "flex",
        alignItems: props.hero ? "center" : "end",
        justifyContent: "center",
        overflow: "hidden",
        textAlign: "left",

    }));

    const ImageOverlay = styled("div")(() => ({
        position: "absolute",
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
    }));

    const TextContent = styled("div")(({theme}) => ({
        position: "relative",
        color: theme.palette.text.primary,
        display: "flex",
        alignItems: "start",
        justifyContent: "start",
        flexDirection: "column",
        width: "100%",
        zIndex: 0, // Added a z-index so that this div stays above the Image component
        marginBottom: props.hero ? 0 : theme.spacing(10)
    }));

    const isPreview = usePreviewContext().isPreview;
    const {
        isWindowExtraSmall,
        isWindowSmall,
        isWindowMedium,
        isWindowLarge,
        isWindowExtraLarge,
        isLoadingBreakpoints,
        lowerThanWindowMedium,
        lowerThanWindowSmall
    } = useWindowSize();

    const isWindowExtraSmallOrSmall = isWindowExtraSmall || isWindowSmall;
    const isWindowMediumOrLarger = isWindowMedium || isWindowLarge || isWindowExtraLarge;

    let headlinePadding = '5px';
    let boxMargin = 4;
    if (isWindowExtraLarge) {
        boxMargin = 6;
    }
    if (isWindowMedium) {
        headlinePadding = '8px';
        boxMargin = 4;
    } else if (isWindowLarge) {
        headlinePadding = '6px';
        boxMargin = 6;
    }

    const px = getTextContentPx(lowerThanWindowSmall, lowerThanWindowMedium);
    const TeaserTextBox = () => {
        return <Grid item lg={6} md={6} xs={12} sx={{mx: 3}}>
            {renderTeaserHeadline(props, isPreview, px)}
            {renderTeaserTitle(props, headlinePadding, isPreview, isWindowExtraSmallOrSmall, px)}
            {renderTeaserText(props, boxMargin, px)}
        </Grid>
    }

    const picture = props.item?.picture ?? (props.item?.stage && props.item?.stage.length > 0 ? props.item?.stage[0].picture : undefined);

    const stageTeaserPositioning = picture?.mediaSettings?.positionForStageteaser;
    const stageTeaserPositionStyle = stageTeaserPositioning
        ? {
            height: "auto",
            minHeight: "100%",
            width: "100%",
            position: "absolute",
            top: stageTeaserPositioning === "top" ? "0" : undefined,
            bottom: stageTeaserPositioning === "bottom" ? "0" : undefined,
        }
        : {};

    const isDataReady = !!(
        props.item &&
        props.item.picture
    );


    if (!isDataReady || isLoadingBreakpoints) {
        return (
            <Placeholder />
        );
    }

    return (<PreviewElementBoxDrawer propertyId={props.item.id}>
            <div data-cypress={props.dataCypress}>
                <HeroImage>
                    {picture ?
                        <>
                            <CCOResponsivePicture dataCypress={"stageTeaserImage"}
                                                  pictureSetting={C014StageCarouselPictureSettings}
                                                  picture={picture}
                                                  hasPriority={!stageTeaserPositioning}
                                                  alt={getAltText(picture.alt, picture.title)}
                                                  imageStyle={{objectFit: "cover", width: "100%", height: "100%", ...stageTeaserPositionStyle}}
                                                  pictureStyle={{
                                                      objectFit: "cover",
                                                      width: "100%",
                                                      height: "100%",
                                                      position: "absolute"
                                                  }}/>
                            <ImageOverlay/>
                        </> : <></>
                    }
                    <TextContent>
                        <CCOContainer width={"full"}>
                            <Grid container>
                                {isWindowMediumOrLarger ? <TeaserTextBox/> : null}
                            </Grid>
                        </CCOContainer>
                    </TextContent>
                </HeroImage>
                {isWindowExtraSmallOrSmall ?
                    <Box marginTop={3} marginBottom={2} padding={'4px'}><TeaserTextBox/></Box> : null}
            </div>
        </PreviewElementBoxDrawer>
    );
}

function renderTeaserHeadline(props: Props, isPreview: boolean, px: number) {
    return !props.hero && props.item?.headline
        && <Typography variant='overline'
                       sx={headlineStyle(px)}
                       {...metaDataProperty(isPreview, 'properties.headline', props.item.id)}>
            {props.item?.headline}
        </Typography>
}

function renderTeaserTitle(props: Props, padding: string, isPreview: boolean, isWindowExtraSmallOrSmall: boolean, px: number) {
    return props.item?.teaserTitle
        && <div style={titleBoxStyle}>
            <Typography variant='h3'
                        component={props.item?.teaserSettings?.activateH1 ? 'h1' : 'div'}
                        sx={titleStyle(padding, isWindowExtraSmallOrSmall, px)}>
              <span {...metaDataProperty(isPreview, 'properties.teaserTitle', props.item.id)}>
                {props.item?.teaserTitle}
              </span>
            </Typography>
        </div>
}

function renderTeaserText(props: Props, boxMargin: number, px: number) {
    const teaserText = props.item?.teaserText;
    const hasTeaserButton = hasTeaserTarget(props);
    const marginBottom = hasTeaserButton ? MARGINS['p']?.bottom : '0';
    return (teaserText || hasTeaserButton)
        && <Box sx={textBoxStyle(boxMargin, marginBottom, px)}>
            {teaserText && <CCORichText metadataId={props.item.id} propertyName='properties.teaserText'
                                        textAsTree={teaserText.textAsTree}/>}
            {hasTeaserButton && <TeaserCardCallToAction item={props.item}
                                                        callToActionType={CallToActionType.BUTTON}
                                                        callToActionCustomText={props.item?.carouselSettings?.callToActionCustomText}
            />}
        </Box>;
}

function hasTeaserTarget(props: Props): boolean {
    return props.item?.carouselSettings?.callToActionEnabled
        && props.item?.teaserTarget;
}

function getTextContentPx(lowerThanWindowSmall: boolean, lowerThanWindowMedium: boolean): number {
    if (lowerThanWindowSmall) {
        return 0;
    }
    if (lowerThanWindowMedium) {
        return 2;
    }
    return 4;
}

export default C014StageTeaser;